import { Switch } from "@xola/ui-kit";
import {
    isAutoGeneratedName,
    isExperienceProduct,
    isPackageProduct,
    isPurchaseEmailRequired,
    isPurchasePhoneRequired,
} from "common";
import { useFormikContext } from "formik";
import { produce } from "immer";
import React, { useEffect, useState } from "react";
import { FieldResponse, FieldType, Product, Traveler } from "types";
import { useDebouncedCallback } from "use-debounce";
import shallow from "zustand/shallow";
import { EditedFormValues, EditPrimaryContactModal } from "../Customers/Contacts/EditPrimaryContactModal";
import { MergeContactsModal } from "../Customers/Contacts/MergeContactsModal";
import { CheckoutFormValues } from "./CheckoutPurchaseScreen.types";
import { CreatePurchaseCustomerName } from "./CreatePurchaseCustomerName";
import { CreatePurchaseEmail } from "./CreatePurchaseEmail";
import { CreatePurchasePhoneNumber } from "./CreatePurchasePhoneNumber";
import {
    getPurchaserValues,
    handleGuestListChange,
    isPurchaserEmailOrPhoneUpdated,
    syncGuestFormResponse,
} from "./helpers/PurchaseCreateHelpers";
import { SameAsRecipientToggle } from "./SameAsRecipientToggle";
import { SearchCustomerModal } from "./SearchCustomerModal";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { queryClient } from "@/api/queryClient";
import { MarketingConsentField } from "@/components/PurchaseCreate/GuestQuestionnaire/MarketingConsentField";
import { useSeller } from "@/hooks/useAccount";
import { usePrevious } from "@/hooks/usePrevious";
import { useModal } from "@/hooks/useVisibility";
import { resetPaymentLineItem } from "@/models/CreatePurchaseState";
import { hasUniqueGuestIdentification } from "@/models/Experience";
import { getPrimaryGuest } from "@/models/Guest";
import { isSharedDemographicsPackage } from "@/models/Package";
import { isExperiencePurchaseItem, isMembershipItem, isMerchandiseItem } from "@/models/PurchaseItem";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface CreatePurchaseCustomerFieldsProps {
    product: Product;
}

/** TODO: Try to reduce useEffects used in this component and directly dispatch action from the place where dependencies are changing */
export const CreatePurchaseCustomerFields = React.memo(({ product }: CreatePurchaseCustomerFieldsProps) => {
    const seller = useSeller();

    const [showCustomerSearchModal, setShowCustomerSearchModal] = useState(false);

    const {
        isVisible: isVisibleEditContactModal,
        show: showEditContactModal,
        hide: hideEditContactModal,
    } = useModal(false);

    const [mergeContactsEditedValues, setMergeContactsEditedValues] = useState<EditedFormValues>();

    const guests = useCreatePurchaseStore((state) => state.getActiveItem().guests) ?? [];
    const primaryGuest = getPrimaryGuest(guests);

    const customerName = useCustomerDetailsStore((state) => state.customerName);

    const [isMist, isExperienceItem, isMembership, isMerchandise, isOneStepCheckout] = useCreatePurchaseStore(
        (state) => [
            state.isMist(),
            isExperiencePurchaseItem(state.getActiveItem()),
            isMembershipItem(state.getActiveItem()),
            isMerchandiseItem(state.getActiveItem()),
            state.isOneStepCheckout(),
        ],
        shallow,
    );

    const [organizer, isPurchaserOrganizer] = useCreatePurchaseStore(
        (state) => [state.organizer, state.isPurchaserOrganizer],
        shallow,
    );
    const isSharedDemographicPackage = isPackageProduct(product) && isSharedDemographicsPackage(product);
    const isAttendeeUnique = isExperienceProduct(product) ? hasUniqueGuestIdentification(product) : true;

    const previousPrimaryGuestId = usePrevious(primaryGuest?.id);

    const [initialGuests, setInitialGuests] = useState(guests);
    const [initialCustomerName, setInitialCustomerName] = useState<string>("");

    const { values, setFieldValue, setErrors, setValues } = useFormikContext<CheckoutFormValues>();

    const requireEmailEnabled = isPurchaseEmailRequired(seller);
    const requirePhoneEnabled = isPurchasePhoneRequired(seller);

    /** Updates the name of activeItem > primary guest */
    const setDebouncedPrimaryGuestName = useDebouncedCallback((name: string) => {
        if (primaryGuest?.name === name) {
            return;
        }

        const guests = useCreatePurchaseStore.getState().getActiveItem().guests ?? [];

        handleGuestListChange(
            guests.map((guest) => {
                return guest.primary ? { ...guest, name: name } : guest;
            }),
        );
    }, 300);

    /**
     * Sets purchase store customer/purchaser details from Formik values with a debounce of "300"
     *
     * TODO: This is a temporary solution for customerName field values lag. Optimise it by fixing re-renders and dependencies used in components
     * The main issue is un-necessary re-renders of checkout page components when any value changes in createPurchaseStore.
     * For example, when `purchase.customerName` is changed, most of the components on the page re-renders. When it should only affect this component and `CreatePurchaseCustomerName`
     * */
    const setDebouncedCustomerFields = useDebouncedCallback(() => {
        const { customerName, customerEmail, phone } = values;

        const newCustomerName = customerName ?? organizer?.name;

        useCustomerDetailsStore.setState((state) => ({
            customerName: state.customerName !== newCustomerName ? newCustomerName : state.customerName,
            customerEmail: organizer?.email ?? customerEmail,
            phone: organizer?.phone ?? phone,
        }));
    }, 300);

    /** This method should be called only for one-step checkout or shared demographics package purchase when a user has added only one demographic */
    const setPrimaryGuestNameToCustomerName = () => {
        if (guests.length !== 1) {
            return;
        }

        const purchaserName = guests[0].name;

        // Skip updating the name if the current name is auto-generated
        // and the new name is a valid custom name (non-empty and not auto-generated).
        // This prevents overwriting an auto-generated name with a custom one.
        if (
            isAutoGeneratedName(purchaserName) &&
            values.customerName !== "" &&
            !isAutoGeneratedName(values.customerName)
        ) {
            return;
        }

        useCustomerDetailsStore.setState((state) => ({
            customerName: purchaserName ?? state.customerName,
        }));

        setFieldValue("customerName", purchaserName ?? customerName);
    };

    const setPurchaserValuesToCustomer = useDebouncedCallback(() => {
        const { purchase } = useCreatePurchaseStore.getState();
        const { name, email, phone, id } = getPurchaserValues(purchase);

        if (id) {
            useCreatePurchaseStore.setState({ purchaser: id });
        }

        /** For single-item checkout, when we have only one guest and "Purchaser is attending" toggled on, we don't want to set the `purchase.customerName` to placeholder name received by `getPurchaserValue`.
         * Instead, we'll use the value of name questionnaire field directly */
        const purchaserName = isOneStepCheckout && guests.length === 1 ? guests[0].name : name;

        const updateCustomerNameWithPurchaserName =
            !isAutoGeneratedName(purchaserName) ||
            values.customerName === "" ||
            isAutoGeneratedName(values.customerName);

        useCustomerDetailsStore.setState((state) => ({
            customerName:
                (isOneStepCheckout && updateCustomerNameWithPurchaserName) ||
                (isSharedDemographicPackage && !isAutoGeneratedName(purchaserName))
                    ? purchaserName ?? state.customerName
                    : state.customerName || purchaserName,
            customerEmail: state.customerEmail || email,
            phone: state.phone || phone,
        }));
        const { customerName, customerEmail, phone: customerPhone } = useCustomerDetailsStore.getState();

        setValues({
            ...values,
            customerName:
                (isOneStepCheckout && updateCustomerNameWithPurchaserName) || isSharedDemographicPackage
                    ? purchaserName ?? customerName
                    : customerName || purchaserName,
            customerEmail: customerEmail || email,
            phone: customerPhone || phone,
        });
    }, 300);

    // There was a showcase request to retain the customer data after resuming the purchase. It's handled in one line Search Customer case but couldn't think of anything better for manually typed data, as a user can navigate outside of the page anytime and is expected to see the exact same stuff on the resume
    useEffect(() => {
        /** When form values are updated, we also need to update the purchase store (purchaser) data  */

        setDebouncedCustomerFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.customerName, values.customerEmail, values.phone]);

    useEffect(() => {
        /** If "Purchaser is attending" is toggled on, and only one guest is added, we need to also update that guest name when customerName is updated */
        if (isPurchaserOrganizer || ((isMembership || isMerchandise) && !isMist)) {
            setDebouncedPrimaryGuestName(values.customerName);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to run this side effect when `isPurchaserOrganizer` is updated
    }, [values.customerName]);

    useEffect(() => {
        if (organizer) {
            useCustomerDetailsStore.setState({
                emailRequired: !!organizer?.email,
                phoneRequired: !!organizer?.phone,
            });
        }
    }, [organizer]);

    useEffect(() => {
        /** Whenever the primary guest name is changed, we need to also update the purchaser name */
        if (isPurchaserOrganizer && primaryGuest && primaryGuest?.name !== values.customerName) {
            if (isOneStepCheckout && guests.length === 1) {
                setPrimaryGuestNameToCustomerName();
                return;
            }

            setPurchaserValuesToCustomer();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOneStepCheckout, isPurchaserOrganizer, guests.length, primaryGuest?.name]);

    useEffect(() => {
        if (!isPurchaserOrganizer) {
            return;
        }

        /** When we have only one guest available and "email" or "phone", for that is changed from questionnaire modal, we need to also sync those changes to purchaser details  */
        const { purchase } = useCreatePurchaseStore.getState();
        const { customerEmail: emailFromStore, phone: phoneFromStore } = useCustomerDetailsStore.getState();
        if (isPurchaserEmailOrPhoneUpdated(purchase)) {
            const { email, phone } = getPurchaserValues(purchase);
            setFieldValue("customerEmail", email || emailFromStore);
            setFieldValue("phone", phone || phoneFromStore);
        }

        /** When guests are changed, and the pre-selected customer/organizer is removed from guests list, we need to set the new primary guest */
        if (guests.length > 1 && organizer && !guests.some((guest) => guest.id === organizer.id)) {
            setPurchaserValuesToCustomer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guests]);

    useEffect(() => {
        if (initialGuests) {
            const newGuest = guests.find(
                (guest) => !initialGuests?.find((initialGuest) => guest.id === initialGuest?.id),
            );

            if (newGuest) {
                setInitialGuests((previous) => [...previous, newGuest]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guests]);

    const updatePrimaryGuestFormResponse = (isPurchaserOrganizer: boolean, id?: ID) => {
        const guests = useCreatePurchaseStore.getState().getActiveItem().guests ?? [];

        const formExists = !!useCreatePurchaseStore.getState().getForm();

        const newGuests = produce(guests, (draft) => {
            for (const guest of draft) {
                const isOrganizer = guest.id === id;

                // Find the initial guest corresponding to the previous primary guest ID
                const initialGuest = initialGuests.find((initialGuest) => initialGuest.id === previousPrimaryGuestId);

                const updatedGuestName =
                    isOrganizer && previousPrimaryGuestId
                        ? isPurchaserOrganizer && customerName
                            ? customerName
                            : initialGuest?.name ?? ""
                        : guest.name;

                guest.name = updatedGuestName;
                guest.organizer = isPurchaserOrganizer ? isOrganizer : false;

                if (isOrganizer && formExists && primaryGuest?.formResponse) {
                    const localFields = new Set([FieldType.EMAIL, FieldType.PHONE]);
                    const updatedFormFields = primaryGuest?.formResponse?.fields?.map((field) =>
                        localFields.has(field.type)
                            ? // incase of organizer is attending, we need to set the form fields to input organizer details or set to empty
                              { ...field, value: isPurchaserOrganizer ? getOrganizerValueForForm(field, values) : "" }
                            : field,
                    );

                    guest.formResponse = { fields: updatedFormFields };
                }
            }
        });
        handleGuestListChange(newGuests);
    };

    const setPurchaser = (isPurchaserOrganizer: boolean, id?: ID) => {
        useCreatePurchaseStore.setState({ purchaser: id });

        updatePrimaryGuestFormResponse(isPurchaserOrganizer, id);
    };

    const handlePurchaseToggle = (value: boolean) => {
        const { purchase } = useCreatePurchaseStore.getState();
        const { id } = getPurchaserValues(purchase);

        useCreatePurchaseStore.setState({ isPurchaserOrganizer: value });
        setPurchaser(value, id);

        if (value && !values.customerName && guests.length > 0) {
            /** When Organizer is attending is toggled on, and we don't have any customer name, Set it to the currently selected primary guest placeholder name */
            const primaryGuest = guests.find((guest) => guest.primary) ?? guests[0];
            setFieldValue(
                "customerName",
                initialGuests.find((initialGuest) => initialGuest.id === primaryGuest.id)?.name ?? "",
            );
        }
    };

    const setCustomerFieldValues = (customerName = "", customerEmail = "", phone = "") => {
        setValues({
            ...values,
            customerName: customerName,
            customerEmail: customerEmail,
            phone: phone,
        });
        setErrors({ customerName: "", customerEmail: "", phone: "", deposit: "" });
    };

    const setEmailAndPhoneToPurchaser = (email = "", phone = "") => {
        const form = useCreatePurchaseStore.getState().getForm();
        syncGuestFormResponse(FieldType.EMAIL, email, form);
        syncGuestFormResponse(FieldType.PHONE, phone, form);
    };

    const updateCustomerDetails = (user: Traveler) => {
        useCustomerDetailsStore.getState().actions.initialize(user.name, user.email, user.phone);
        useCreatePurchaseStore.setState({ organizer: user });
        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            draft.customerEmail = user.email;
        });

        if (useCreatePurchaseStore.getState().isPurchaserOrganizer) {
            setEmailAndPhoneToPurchaser(user.email, user.phone);
        }

        setCustomerFieldValues(user.name, user.email, user.phone);
    };

    const handleCustomerSearch = (user: Traveler) => {
        updateCustomerDetails(user);
        setShowCustomerSearchModal(false);
    };

    const handleRemoveCustomer = () => {
        setCustomerFieldValues();

        useCustomerDetailsStore.setState({
            emailRequired: requireEmailEnabled,
            phoneRequired: requirePhoneEnabled,
        });

        if (useCreatePurchaseStore.getState().isPurchaserOrganizer) {
            setEmailAndPhoneToPurchaser();
        }

        useCustomerDetailsStore.getState().actions.initialize("", "", "");

        resetPaymentLineItem();

        useCreatePurchaseStore.setState({ organizer: undefined });
    };

    const handleMergeContactsSuccess = (mergedContactId: ID) => {
        setMergeContactsEditedValues(undefined);

        const itemInCache = queryClient.getQueryData<Traveler>(["users", mergedContactId]);
        if (itemInCache) {
            updateCustomerDetails(itemInCache);
        }
    };

    const showOrganizerAttendingToggle =
        isAttendeeUnique && isExperienceItem && (isSharedDemographicPackage || !isMist);

    return (
        <>
            <div className="mb-4 flex items-center space-x-4">
                <h3>Organizer Details</h3>
                <SameAsRecipientToggle />
            </div>

            <CreatePurchaseCustomerName
                product={product}
                isPurchaserAttending={isPurchaserOrganizer}
                setInitialCustomerName={(customerName) => setInitialCustomerName(customerName)}
                onShow={() => setShowCustomerSearchModal(true)}
                onEditContactClick={showEditContactModal}
                onRemove={handleRemoveCustomer}
            />

            <CreatePurchaseEmail onEditContactClick={showEditContactModal} />

            <CreatePurchasePhoneNumber onEditContactClick={showEditContactModal} />

            <MarketingConsentField />

            {showOrganizerAttendingToggle && (
                <Switch.Group className="-mb-14">
                    <Switch size="medium" isChecked={isPurchaserOrganizer} onChange={handlePurchaseToggle} />
                    <Switch.Label className="ml-2">Organizer is attending</Switch.Label>
                </Switch.Group>
            )}

            <SearchCustomerModal
                isOpen={showCustomerSearchModal}
                initialSearch={isAutoGeneratedName(initialCustomerName) ? "" : initialCustomerName}
                onClose={() => setShowCustomerSearchModal(false)}
                onSubmit={handleCustomerSearch}
            />

            {organizer && (
                <EditPrimaryContactModal
                    contact={organizer}
                    isOpen={isVisibleEditContactModal}
                    onSuccess={handleCustomerSearch}
                    onClose={hideEditContactModal}
                    onDuplicate={(editedValues: EditedFormValues) => setMergeContactsEditedValues(editedValues)}
                />
            )}

            {organizer && (
                <MergeContactsModal
                    contact={organizer}
                    editedValues={mergeContactsEditedValues}
                    isOpen={!!mergeContactsEditedValues}
                    onClose={() => setMergeContactsEditedValues(undefined)}
                    onSuccess={(mergedContactId) => handleMergeContactsSuccess(mergedContactId)}
                />
            )}

            <hr className="my-8 text-gray-light" />
        </>
    );
});

const getOrganizerValueForForm = (field: FieldResponse, values: CheckoutFormValues) => {
    return field.type === FieldType.EMAIL
        ? values.customerEmail
        : field.type === FieldType.PHONE
        ? values.phone
        : field.value;
};

CreatePurchaseCustomerFields.displayName = "CreatePurchaseCustomerFields";
